import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Copy, Plus, Info, ChevronDown, ChevronUp, Check, Filter, Download, Search } from "lucide-react";
import Cookies from "js-cookie";
import storage from "utils/Storage";
import { API } from "utils/Helper";

const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
`;

const PageDesc = styled.p`
  margin-top: 8px;
  color: #b1b6bb;
  font-size: 14px;
  line-height: 1.6;
`;

const StatsWrapperCard = styled.div`
  background: #1a1d1e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  position: relative;

  .header-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: #b1b6bb;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .value {
    font-size: 24px;
    font-weight: 900;
    color: ${props => props.color || "#ffffff"};
    margin-top: 8px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #3a4142;
  margin: 20px 0;
`;

const TooltipContainer = styled.span`
  position: relative;
  display: inline-flex;
  cursor: help;

  &:hover .tooltip-box {
    opacity: 1;
    pointer-events: auto;
  }
`;

const TooltipBox = styled.span`
  pointer-events: none;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 100%;
  margin-bottom: 8px;
  width: 280px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  color: #f5f6f7;
  font-size: 12px;
  padding: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 30;
  white-space: pre-line;
  font-weight: normal;
  text-transform: none;
  letter-spacing: normal;
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const ActionIconButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: #292d2e;
  border: 1px solid #3a4142;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(58, 65, 66, 0.4);
  }
`;

const CompareButton = styled.button`
  flex: 1;
  height: 44px;
  border-radius: 6px;
  background: #292d2e;
  border: 1px solid #3a4142;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0 16px;

  &:hover {
    background: rgba(58, 65, 66, 0.4);
  }
`;

const CreateBtn = styled.button`
  width: 100%;
  height: 48px;
  background: #1fc16b;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const SortContainer = styled.div`
  position: relative;
  margin-top: 20px;
  margin-bottom: 20px;
  
  .sort-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
  }
`;

const SortDropdownTrigger = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;
`;

const SortDropdownMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  overflow: hidden;
  z-index: 40;

  button {
    width: 100%;
    text-align: left;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: #f5f6f7;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(58, 65, 66, 0.4);
    }

    &.active {
      background: rgba(31, 193, 107, 0.15);
      font-weight: bold;
    }
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 15px;
`;

const CampaignCard = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: rgba(58, 65, 66, 0.2);
  }

  .title {
    flex: 1;
    font-weight: 600;
    font-size: 14px;
    color: #ffffff;
    span {
      color: #b1b6bb;
      font-weight: normal;
      margin-left: 6px;
    }
  }

  .meta {
    font-size: 13px;
    color: #b1b6bb;
    display: flex;
    align-items: center;
    gap: 16px;
    
    span.val {
      color: #ffffff;
      font-weight: 700;
    }
  }
`;

const CardExpandedContent = styled.div`
  border-top: 1px solid #3a4142;
  padding: 20px;
  background: #1a1d1e;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .left-side {
    .sec-title {
      font-size: 14px;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 15px;
    }

    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid rgba(58, 65, 66, 0.2);
      font-size: 13px;
      &:last-child {
        border-bottom: none;
      }
      .lbl {
        color: #b1b6bb;
      }
      .val {
        color: #ffffff;
        font-weight: 600;
      }
    }
  }

  .right-side {
    .sec-title {
      font-size: 14px;
      font-weight: 700;
      color: #ffffff;
    }
    .desc {
      font-size: 13px;
      color: #b1b6bb;
      margin-top: 8px;
    }
    .copy-wrap {
      display: flex;
      margin-top: 12px;
      height: 44px;
      border-radius: 6px;
      overflow: hidden;
      border: 1px solid #3a4142;
      background: #292d2e;

      .url {
        flex: 1;
        padding: 0 12px;
        display: flex;
        align-items: center;
        font-size: 13px;
        color: #ffffff;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      button {
        width: 44px;
        border: none;
        background: #3a4142;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          background: #4f5a5c;
        }
      }
    }
  }
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  font-size: 13px;
  color: #b1b6bb;

  .buttons {
    display: flex;
    gap: 8px;
    
    button {
      height: 32px;
      padding: 0 12px;
      background: #292d2e;
      border: 1px solid #3a4142;
      border-radius: 6px;
      color: #ffffff;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      &:not(:disabled):hover {
        background: rgba(58, 65, 66, 0.4);
      }
    }
  }
`;

const CustomModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const CustomModalContent = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);

  .modal-title {
    font-size: 18px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .input-label {
    font-size: 13px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 8px;
    span {
      color: #ef4444;
    }
  }

  input {
    width: 100%;
    height: 44px;
    padding: 0 12px;
    background: #1a1d1e;
    border: 1px solid #3a4142;
    border-radius: 6px;
    color: #ffffff;
    outline: none;
    margin-bottom: 16px;
    &:focus {
      border-color: #1fc16b;
    }
  }

  .link-preview {
    height: 44px;
    padding: 0 12px;
    background: #1a1d1e;
    border: 1px solid #3a4142;
    border-radius: 6px;
    color: #b1b6bb;
    display: flex;
    align-items: center;
    font-size: 13px;
    margin-bottom: 20px;
  }

  .btn-row {
    display: flex;
    gap: 12px;
    margin-top: 24px;
    button {
      flex: 1;
      height: 44px;
      border-radius: 6px;
      font-weight: bold;
      font-size: 14px;
      cursor: pointer;
    }
    .cancel {
      background: #3a4142;
      border: none;
      color: #ffffff;
      &:hover {
        background: #4f5a5c;
      }
    }
    .submit {
      background: #1fc16b;
      border: none;
      color: #ffffff;
      &:hover {
        opacity: 0.9;
      }
    }
  }
`;

const SORT_OPTIONS = [
  "Date Created: New to Old",
  "Date Created: Old to New",
  "First Time Depositor Count: High to Low",
  "First Time Depositor Count: Low to High",
  "Overall Commission: High to Low",
  "Overall Commission: Low to High",
  "Referred Users: High to Low",
  "Referred Users: Low to High",
];

const ComparisonGrid = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  overflow-x: auto;
  margin-top: 20px;
  
  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    
    th, td {
      padding: 16px;
      text-align: right;
      font-size: 14px;
      border-bottom: 1px solid rgba(58, 65, 66, 0.2);
    }
    
    th:first-child, td:first-child {
      text-align: left;
      font-weight: 600;
      color: #b1b6bb;
    }
    
    th {
      font-weight: 800;
      color: #ffffff;
    }
    
    td {
      color: #ffffff;
      font-weight: 700;
    }
  }
`;

const getUserId = () => {
  let uid = Cookies.get("uid");
  if (!uid) {
    try {
      const token = storage.getKey("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        uid = payload.id || payload.uid;
      }
    } catch (e) {}
  }
  return uid ? Number(uid) : null;
};

const Campaigns = () => {
  const [campaignsList, setCampaignsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortOpen, setSortOpen] = useState(false);
  const [sort, setSort] = useState(SORT_OPTIONS[0]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);

  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [comparingData, setComparingData] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const LIMIT = 10;
  const uid = getUserId();

  const fetchCampaigns = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/affiliate/campaigns/${uid}`);
      const data = await res.json();
      if (data.success) {
        setCampaignsList(data.campaigns.map(c => ({
          ...c,
          // Fallbacks for frontend keys
          createdAt: c.created_at || new Date().toISOString(),
          hits: c.referrals * 3 || 0, // Simulated hits since not tracked in campaign_links directly
          refs: c.referrals || 0,
          ftd: c.ftd || 0,
          deposits: 0, // Legacy deposits
          commission: parseFloat(c.total_commission) || 0,
          rate: 10
        })));
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [uid]);

  const handleCopy = (link, id) => {
    navigator.clipboard?.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName || !newCode || !uid) return;
    try {
      const res = await fetch(`${API}/api/affiliate/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, name: newName, code: newCode })
      });
      const data = await res.json();
      if (data.success) {
        setNewName("");
        setNewCode("");
        setCreateOpen(false);
        fetchCampaigns();
      } else {
        alert(data.error || "Failed to create campaign");
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
    }
  };

  const filtered = useMemo(() => {
    let list = selectedFilterIds.length > 0 
      ? campaignsList.filter(c => selectedFilterIds.includes(c.id))
      : campaignsList;

    list = [...list];
    switch (sort) {
      case "Date Created: Old to New":
        list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "First Time Depositor Count: High to Low":
        list.sort((a, b) => b.ftd - a.ftd);
        break;
      case "First Time Depositor Count: Low to High":
        list.sort((a, b) => a.ftd - b.ftd);
        break;
      case "Overall Commission: High to Low":
        list.sort((a, b) => b.commission - a.commission);
        break;
      case "Overall Commission: Low to High":
        list.sort((a, b) => a.commission - b.commission);
        break;
      case "Referred Users: High to Low":
        list.sort((a, b) => b.refs - a.refs);
        break;
      case "Referred Users: Low to High":
        list.sort((a, b) => a.refs - b.refs);
        break;
      case "Date Created: New to Old":
      default:
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
    }
    return list;
  }, [campaignsList, sort, selectedFilterIds]);

  const paginated = useMemo(() => {
    return filtered.slice((page - 1) * LIMIT, page * LIMIT);
  }, [filtered, page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / LIMIT));

  const totals = useMemo(() => {
    return filtered.reduce(
      (a, c) => ({
        hits: a.hits + (c.hits || 0),
        refs: a.refs + (c.refs || 0),
        ftd: a.ftd + (c.ftd || 0),
        td: a.td + (c.deposits || 0),
        com: a.com + (c.commission || 0)
      }),
      { hits: 0, refs: 0, ftd: 0, td: 0, com: 0 }
    );
  }, [filtered]);

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      if (compareIds.length < 5) {
        setCompareIds([...compareIds, id]);
      }
    } else {
      setCompareIds(compareIds.filter(x => x !== id));
    }
  };

  if (comparingData) {
    return (
      <Container>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageTitle>Comparison</PageTitle>
          <CompareButton onClick={() => setComparingData(null)}>Back to Campaigns</CompareButton>
        </div>
        
        <ComparisonGrid>
          <table>
            <thead>
              <tr>
                <th>Campaign Name</th>
                {comparingData.map(c => <th key={c.id}>{c.name}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Overall Commission</td>
                {comparingData.map(c => <td key={c.id} style={{ color: "#1fc16b" }}>${c.commission.toFixed(2)} USD</td>)}
              </tr>
              <tr>
                <td>Campaign Hits</td>
                {comparingData.map(c => <td key={c.id}>{c.hits}</td>)}
              </tr>
              <tr>
                <td>Referrals</td>
                {comparingData.map(c => <td key={c.id}>{c.refs}</td>)}
              </tr>
              <tr>
                <td>First Time Deposits</td>
                {comparingData.map(c => <td key={c.id}>{c.ftd}</td>)}
              </tr>
              <tr>
                <td>Total Deposits</td>
                {comparingData.map(c => <td key={c.id}>${c.deposits.toLocaleString()}</td>)}
              </tr>
            </tbody>
          </table>
        </ComparisonGrid>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <PageTitle>Campaigns</PageTitle>
        <div style={{ padding: "40px 0", textAlign: "center", color: "#b1b6bb" }}>Loading campaigns...</div>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Campaigns</PageTitle>
      <PageDesc>See the performance of all your campaigns in one simple view below.</PageDesc>

      <StatsWrapperCard>
        <StatsGrid>
          <StatCard>
            <div className="header-wrap">
              <span>Campaign Hits</span>
              <TooltipContainer>
                <Info size={14} />
                <TooltipBox className="tooltip-box">
                  Campaign Hits: This refers to the number of hits (all clicks or visits from users) from specific campaign.
                  {"\n\n*All data featured across this overview is updated at least once every hour."}
                </TooltipBox>
              </TooltipContainer>
            </div>
            <div className="value">{totals.hits}</div>
          </StatCard>

          <StatCard>
            <div className="header-wrap">
              <span>Referred Users</span>
              <TooltipContainer>
                <Info size={14} />
                <TooltipBox className="tooltip-box">
                  Referred Users: This refers to all users referred through the specific campaign.
                  {"\n\n*All data featured across this overview is updated at least once every hour."}
                </TooltipBox>
              </TooltipContainer>
            </div>
            <div className="value">{totals.refs}</div>
          </StatCard>

          <StatCard>
            <div className="header-wrap">
              <span>First Time Deposits (FTD)</span>
              <TooltipContainer>
                <Info size={14} />
                <TooltipBox className="tooltip-box">
                  First Time Deposits (FTD): This shows new depositors who make their first deposit through your campaign.
                  {"\n\n*All data featured across this overview is updated at least once every hour."}
                </TooltipBox>
              </TooltipContainer>
            </div>
            <div className="value">{totals.ftd}</div>
          </StatCard>

          <StatCard>
            <div className="header-wrap">
              <span>Total Deposits</span>
              <TooltipContainer>
                <Info size={14} />
                <TooltipBox className="tooltip-box">
                  Total Deposits: This refers to all the deposits made across your currently active campaigns.
                  {"\n\n*All data featured across this overview is updated at least once every hour."}
                </TooltipBox>
              </TooltipContainer>
            </div>
            <div className="value">${totals.td.toLocaleString()}</div>
          </StatCard>
        </StatsGrid>

        <Divider />

        <StatCard>
          <div className="header-wrap">
            <span>Overall Commission</span>
            <TooltipContainer>
              <Info size={14} />
              <TooltipBox className="tooltip-box">
                Overall Commission: This refers to the total overall commission (estimated in USD) across your currently active campaigns.
                {"\n\n*All data featured across this overview is updated at least once every hour."}
              </TooltipBox>
            </TooltipContainer>
          </div>
          <div className="value" style={{ color: "#1fc16b" }}>${totals.com.toFixed(2)} USD</div>
        </StatCard>
      </StatsWrapperCard>

      <ActionsBar>
        <ActionIconButton onClick={() => setFilterOpen(true)}>
          <Filter size={16} />
        </ActionIconButton>
        <ActionIconButton>
          <Download size={16} />
        </ActionIconButton>
        {compareMode ? (
          <>
            <div style={{ flex: 1, fontSize: "14px", fontWeight: "600" }}>Select up to 5 campaigns to compare</div>
            <CompareButton onClick={() => { setCompareMode(false); setCompareIds([]); }}>Cancel</CompareButton>
            <CompareButton 
              style={{ background: "#0070f3", border: "none" }}
              disabled={compareIds.length < 2}
              onClick={() => {
                setComparingData(campaignsList.filter(c => compareIds.includes(c.id)));
                setCompareMode(false);
                setCompareIds([]);
              }}
            >Apply</CompareButton>
          </>
        ) : (
          <CompareButton onClick={() => setCompareMode(true)} style={{ flex: 1 }}>Compare</CompareButton>
        )}
      </ActionsBar>

      <CreateBtn style={{ background: "#0070f3", marginBottom: "20px" }} onClick={() => {
        const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
          randomCode += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setNewCode(randomCode);
        setNewName("");
        setCreateOpen(true);
      }}>
        <Plus size={18} /> Create Campaign
      </CreateBtn>

      <SortContainer>
        <div className="sort-label">
          <Filter size={14} />
          <span>Sort</span>
        </div>
        <SortDropdownTrigger onClick={() => setSortOpen(!sortOpen)}>
          <span>{sort}</span>
          <ChevronDown size={16} />
        </SortDropdownTrigger>
        {sortOpen && (
          <SortDropdownMenu>
            {SORT_OPTIONS.map(opt => (
              <button 
                key={opt}
                className={sort === opt ? "active" : ""}
                onClick={() => {
                  setSort(opt);
                  setSortOpen(false);
                  setPage(1);
                }}
              >
                {opt}
              </button>
            ))}
          </SortDropdownMenu>
        )}
      </SortContainer>

      <CardList>
        {paginated.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#b1b6bb", fontStyle: "italic", background: "#292d2e", borderRadius: "6px", border: "1px solid #3a4142" }}>
            No campaigns found.
          </div>
        ) : paginated.map(c => {
          const referralLink = `${window.location.origin}/register?code=${c.code}`;
          const isExpanded = expandedId === c.id;
          return (
            <CampaignCard key={c.id}>
              <CardHeader onClick={() => setExpandedId(isExpanded ? null : c.id)}>
                {compareMode && (
                  <input 
                    type="checkbox"
                    checked={compareIds.includes(c.id)}
                    onChange={(e) => handleCheckboxChange(c.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: "18px", height: "18px", accentColor: "#1fc16b" }}
                  />
                )}
                <div className="title">
                  {c.name}
                  <span>({c.code})</span>
                </div>
                <div className="meta">
                  <div>Hits: <span className="val">{c.hits}</span></div>
                  <div style={{ display: "none" }} className="show-sm-block">FTD: <span className="val">{c.ftd}</span></div>
                  <div>Commission: <span className="val" style={{ color: "#1fc16b" }}>${c.commission}</span></div>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </CardHeader>

              {isExpanded && (
                <CardExpandedContent>
                  <div className="left-side">
                    <div className="sec-title">Performance Summary</div>
                    <div className="detail-row">
                      <span className="lbl">Campaign Created Date</span>
                      <span className="val">{new Date(c.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">Campaign Hits</span>
                      <span className="val">{c.hits}</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">Referred Users</span>
                      <span className="val">{c.refs}</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">First Time Deposits</span>
                      <span className="val">{c.ftd}</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">Total Deposits</span>
                      <span className="val">${c.deposits.toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">Commission Rate</span>
                      <span className="val">{c.rate}%</span>
                    </div>
                    <div className="detail-row">
                      <span className="lbl">Overall Commission</span>
                      <span className="val" style={{ color: "#1fc16b" }}>${c.commission.toFixed(2)} USD</span>
                    </div>
                  </div>
                  <div className="right-side">
                    <div className="sec-title">Share Campaign</div>
                    <div className="desc">Campaign Link</div>
                    <div className="copy-wrap">
                      <div className="url">{referralLink}</div>
                      <button onClick={() => handleCopy(referralLink, c.id)}>
                        {copiedId === c.id ? <Check size={16} color="#1fc16b" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                </CardExpandedContent>
              )}
            </CampaignCard>
          );
        })}
      </CardList>

      <PaginationRow>
        <div>
          {filtered.length > 0 ? `Showing ${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, filtered.length)} of ${filtered.length}` : "No results"}
        </div>
        <div className="buttons">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </PaginationRow>

      {createOpen && (
        <CustomModalOverlay onClick={() => setCreateOpen(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <Plus size={20} />
              <span>Create Campaign</span>
            </div>
            <form onSubmit={handleCreate}>
              <div className="input-label">Campaign Name <span>*</span></div>
              <input 
                type="text"
                required
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                }}
                placeholder="e.g. India Promo Link"
              />

              <div className="input-label">Campaign ID <span>*</span></div>
              <input 
                type="text"
                required
                value={newCode}
                onChange={(e) => {
                  const cleanVal = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  setNewCode(cleanVal);
                }}
                placeholder="e.g. kdmcv8zi"
              />

              <div className="input-label">Referral Link</div>
              <input 
                type="text"
                readOnly
                value={`${window.location.origin}/register?code=${newCode}`}
                style={{ background: "#212526", color: "#8a9094", cursor: "not-allowed" }}
              />

              <div className="btn-row">
                <button type="button" className="cancel" onClick={() => setCreateOpen(false)}>Cancel</button>
                <button type="submit" className="submit">Create Campaign</button>
              </div>
            </form>
          </CustomModalContent>
        </CustomModalOverlay>
      )}

      {filterOpen && (
        <CustomModalOverlay onClick={() => setFilterOpen(false)}>
          <CustomModalContent onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              <Filter size={20} />
              <span>Filter</span>
            </div>
            <div className="input-label">Filter by Campaign Name or Campaign ID</div>
            <div style={{ position: "relative", marginBottom: "16px" }}>
              <input 
                type="text" 
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="Search Campaign Name or Campaign ID"
                style={{ paddingLeft: "36px", marginBottom: 0 }}
              />
              <Search size={16} style={{ position: "absolute", left: "12px", top: "14px", color: "#b1b6bb" }} />
            </div>

            <div style={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px", paddingRight: "4px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                <input 
                  type="checkbox"
                  checked={selectedFilterIds.length === campaignsList.length}
                  onChange={(e) => {
                    setSelectedFilterIds(e.target.checked ? campaignsList.map(c => c.id) : []);
                  }}
                  style={{ width: "16px", height: "16px", accentColor: "#1fc16b" }}
                />
                All ({campaignsList.length})
              </label>
              {campaignsList.filter(c => c.name.toLowerCase().includes(filterQuery.toLowerCase()) || c.code.toLowerCase().includes(filterQuery.toLowerCase())).map(c => (
                <label key={c.id} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                  <input 
                    type="checkbox"
                    checked={selectedFilterIds.includes(c.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFilterIds([...selectedFilterIds, c.id]);
                      } else {
                        setSelectedFilterIds(selectedFilterIds.filter(x => x !== c.id));
                      }
                    }}
                    style={{ width: "16px", height: "16px", accentColor: "#1fc16b" }}
                  />
                  {c.name} ({c.code})
                </label>
              ))}
            </div>

            <div className="btn-row" style={{ marginTop: "20px" }}>
              <button className="cancel" onClick={() => { setSelectedFilterIds([]); setFilterQuery(""); }}>Clear Selection</button>
              <button className="submit" onClick={() => { setFilterOpen(false); setPage(1); }}>Apply</button>
            </div>
          </CustomModalContent>
        </CustomModalOverlay>
      )}
    </Container>
  );
};

export default Campaigns;
